/* eslint-disable no-useless-escape */
import styles from "../../../_common/CrudFormStyles.module.scss";
import { useEffect } from "react";
import { Form, Input, Row, Col, Select, Divider, Badge, Switch, message, Popover, Tag } from "antd";
import { translateKey as t } from "../../../../../locales/translation";
import { faEnvelope, faLock, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UserController } from "../../UserController";
import { observer } from "mobx-react";

const { Option } = Select;

const UserForm = observer(({ userController }: { userController: UserController }) => {
  useEffect(() => {
    let { formMode, user } = userController;

    if (formMode === "edit") {
      user.confirmPassword = user.password;
    }
  }, []);

  const [form] = Form.useForm();
  userController.setFormRef(form);

  const {
    user,
    providers,
    isReadOnly,
    isSelectedUserReadOnly,
    formMode,
    // depots,
    roles,
    formFieldsSize,
    showNonMandatoryFields,
    showFieldsIcon,
  } = userController;

  //--------------------------------------------------------------------------------------------------------------

  const handleInputChange = (e: any) => {
    let value: string = e.target.value;
    let key: string = e.target.name;

    // Prevents the user from using certain special characters and empty spaces
    if (key === "username" && !RegExp("^[a-zA-Z0-9_.@()&-]*$").test(value)) {
      // Instantly cuts the invalid character
      e.target.value = e.target.value.substring(0, e.target.value.length - 1);

      message.warning({
        content: t(
          "lblUsernameMustNotContainThisSpecialCharacterOrSpaces",
          "Username must not contain this Special Character or Spaces"
        ),
        key,
        duration: 4,
      });

      return;
    }
    // Prevents the user from using certain special characters, but allows empty spaces
    if ((key === "firstName" || key === "lastName") && !RegExp("^[ a-zA-Z0-9_.@()&-]*$").test(value)) {
      // Instantly cuts the invalid character
      e.target.value = e.target.value.substring(0, e.target.value.length - 1);

      if (key === "firstName") {
        message.warning({
          content: t(
            "lblFirstNameMustNotContainThisSpecialCharacter",
            "First Name must not contain this Special Character"
          ),
          key,
          duration: 4,
        });
      } else {
        message.warning({
          content: t(
            "lblLastNameMustNotContainThisSpecialCharacter",
            "Last Name must not contain this Special Character"
          ),
          key,
          duration: 4,
        });
      }

      return;
    }

    if (key === "password" && userController.formMode === "edit") {
      userController.passwordChanged = true;
    }

    userController.setUser(value, key);
  };

  const handleProviderChange = (value: string) => {
    userController.setUser(value, "provider");

    form.setFieldsValue({
      provider: value,
    });
    if (value !== "local") {
      // Cleans passwords fields
      form.setFieldsValue({
        password: null,
        confirmPassword: null,
      });
      // Actually cleans payload's values
      userController.setUser("", "password");
      userController.setUser("", "confirmPassword");
    }
  };

  const handleRoleChange = (roleRef: string) => {
    userController.setUser(roleRef, "roles");
    form.setFieldsValue({
      roles: [roleRef],
    });
  };

  const handlePasswordBadgeField = (type) => {
    const { user, formMode } = userController;

    let flagState: boolean;

    if (formMode === "create") {
      switch (type) {
        case "password":
          flagState =
            !user.password && user.provider !== "ldapauth" && user.provider !== "azuread" && user.provider !== "google";
          break;
        case "confirmPassword":
          flagState = false;
          break;
      }
    }

    if (formMode === "edit") {
      switch (type) {
        case "password":
          flagState = false;
          break;
        case "confirmPassword":
          flagState = false;
          break;
      }
    }

    return flagState;
  };

  const dropdownFilterOption = (inp: string, opt: any) =>
    opt.props.children?.toString().toLowerCase().indexOf(inp.toLowerCase()) >= 0;

  //--------------------------------------------------------------------------------------------------------------

  const renderPasswordFields = () => {
    const { user, isReadOnly, isSelectedUserReadOnly, formMode, formFieldsSize, showFieldsIcon, showPasswords } =
      userController;

    let content: any = null;
    content = (
      <Row
        gutter={20}
        className={styles.newRow}
        style={formFieldsSize === "small" && { marginTop: 0, marginBottom: -15 }}
      >
        {formMode === "edit" && (
          <Col span={4}>
            <Form.Item
              colon={false}
              label={<span className={styles.formTitle}>{t("lblChangePassword", "Change Password")}</span>}
            >
              <Switch
                style={{ marginRight: 10 }}
                checked={userController.changePassword}
                disabled={isReadOnly || isSelectedUserReadOnly}
                onChange={() => {
                  userController.changePassword = !userController.changePassword;
                }}
              />
            </Form.Item>
          </Col>
        )}
        {(formMode === "create" || (formMode === "edit" && userController.changePassword)) && (
          <Col span={formMode !== "edit" ? 12 : 10}>
            <Form.Item
              colon={false}
              name="password"
              label={
                <Badge dot={handlePasswordBadgeField("password")}>
                  <span className={styles.formTitle}>
                    {formMode === "create" ? t("lblPassword", "Password") : t("lblNewPassword", "New Password")}
                  </span>
                </Badge>
              }
              rules={[
                { required: user.provider === "local", message: t("lblPasswordIsRequired", "Password is required") },
              ]}
            >
              <Input
                name="password"
                onChange={handleInputChange}
                disabled={user.provider !== "local" || isReadOnly || isSelectedUserReadOnly}
                type={showPasswords ? "text" : "password"}
                autoComplete="new-password"
                prefix={showFieldsIcon && <FontAwesomeIcon icon={faLock} style={{ color: "rgba(0,0,0,.25)" }} />}
                size={formFieldsSize}
              />
            </Form.Item>
          </Col>
        )}
        {(formMode === "create" || (formMode === "edit" && userController.changePassword)) && (
          <Col span={formMode !== "edit" ? 12 : 10}>
            <Form.Item
              colon={false}
              name="confirmPassword"
              label={
                <Badge dot={handlePasswordBadgeField("confirmPassword")}>
                  <span className={styles.formTitle}>
                    {formMode === "create"
                      ? t("lblConfirmPassword", "Confirm Password")
                      : t("lblConfirmNewPassword", "Confirm New Password")}
                  </span>
                </Badge>
              }
              rules={[
                {
                  required: user.provider === "local" && user.password,
                  message: t("lblConfirmPasswordIsRequired", "Confirm Password is required."),
                },
              ]}
            >
              <Input
                name="confirmPassword"
                onChange={handleInputChange}
                disabled={!user.password || user.provider !== "local" || isReadOnly || isSelectedUserReadOnly}
                type={showPasswords ? "text" : "password"}
                autoComplete="new-password"
                prefix={showFieldsIcon && <FontAwesomeIcon icon={faLock} style={{ color: "rgba(0,0,0,.25)" }} />}
                size={formFieldsSize}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    );

    return content;
  };

  return (
    <Form
      form={form}
      className={styles.form}
      requiredMark={false}
      initialValues={{
        username: user.username,
        provider: user.provider,
        roles: user.roles,
        password: user.password,
        confirmPassword: user.confirmPassword,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      }}
      layout="vertical"
    >
      <Row className={styles.newRow} gutter={20} style={formFieldsSize === "small" && { marginTop: 0 }}>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!user.username}>
                <span className={styles.formTitle}>{t("lblUsername", "Username")}</span>
              </Badge>
            }
            name="username"
            rules={[{ required: true, whitespace: true, message: t("lblUsernameIsRequired", "Username is required") }]}
          >
            <Input
              onChange={handleInputChange}
              name="username"
              disabled={isReadOnly || isSelectedUserReadOnly || formMode === "edit"}
              autoFocus={formMode === "create"}
              maxLength={99}
              prefix={showFieldsIcon && <FontAwesomeIcon icon={faLock} style={{ color: "rgba(0,0,0,.25)" }} />}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!user.email}>
                <span className={styles.formTitle}>{t("lblEmail", "Email")}</span>
              </Badge>
            }
            name="email"
            rules={[
              {
                required: true,
                whitespace: true,
                message: t("lblEmailIsRequired", "Email is required"),
              },
              {
                pattern:
                  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: t("lblEmailFormatIsIncorrect", "Email format is incorrect"),
              },
            ]}
          >
            <Input
              onChange={handleInputChange}
              name="email"
              disabled={isReadOnly || isSelectedUserReadOnly}
              maxLength={99}
              prefix={showFieldsIcon && <FontAwesomeIcon icon={faEnvelope} style={{ color: "rgba(0,0,0,.25)" }} />}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={20} className={styles.newRow} style={formFieldsSize === "small" && { marginTop: 0 }}>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!user.firstName}>
                <span className={styles.formTitle}>{t("lblFirstName", "First Name")}</span>
              </Badge>
            }
            name="firstName"
            rules={[
              { required: true, whitespace: true, message: t("lblFirstNameIsRequired", "First Name is required") },
            ]}
          >
            <Input
              name="firstName"
              onChange={handleInputChange}
              disabled={isReadOnly || isSelectedUserReadOnly}
              maxLength={99}
              prefix={showFieldsIcon && <FontAwesomeIcon icon={faLock} style={{ color: "rgba(0,0,0,.25)" }} />}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <Badge dot={!user.lastName}>
                <span className={styles.formTitle}>{t("lblLastName", "Last Name")}</span>
              </Badge>
            }
            name="lastName"
            rules={[{ required: true, whitespace: true, message: t("lblLastNameIsRequired", "Last Name is required") }]}
          >
            <Input
              name="lastName"
              onChange={handleInputChange}
              disabled={isReadOnly || isSelectedUserReadOnly}
              maxLength={99}
              prefix={showFieldsIcon && <FontAwesomeIcon icon={faLock} style={{ color: "rgba(0,0,0,.25)" }} />}
              size={formFieldsSize}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider
        style={formFieldsSize === "small" ? { marginTop: 5, marginBottom: 0 } : { marginTop: 20, marginBottom: 10 }}
      />

      <Row
        gutter={20}
        className={styles.newRow}
        style={formFieldsSize === "small" && { marginTop: 0, marginBottom: -5 }}
      >
        <Col span={12}>
          <Form.Item
            label={<span className={styles.formTitle}>{t("lblProvider", "Provider")}</span>}
            colon={false}
            name="provider"
          >
            <Select
              value={user.provider}
              onChange={handleProviderChange}
              showSearch={true}
              filterOption={(input, option) => dropdownFilterOption(input, option)}
              disabled={isReadOnly || isSelectedUserReadOnly}
              size={formFieldsSize}
            >
              {providers.map((p) => {
                return (
                  <Option key={p._id} value={p.ref}>
                    {p.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            colon={false}
            label={
              <>
                <Badge dot={user.roles?.length === 0}>
                  <span className={styles.formTitle}>{t("lblRole", "Role")}</span>
                </Badge>
                {user.roles?.length > 1 && (
                  <Popover
                    title={t("lblThisUserHasMoreThanOneRoleAssigned", "This User has more than one role assigned!")}
                    content={
                      <div>
                        <strong>{t("lblRoles", "Roles")}</strong>
                        <span>: </span>
                        {user.roles?.map((role, index) => (
                          <Tag key={index} color="blue">
                            {role}
                          </Tag>
                        ))}
                      </div>
                    }
                  >
                    <FontAwesomeIcon icon={faWarning} style={{ marginTop: 2, marginLeft: -3 }} />
                  </Popover>
                )}
              </>
            }
            name="roles"
            rules={[{ required: true, message: t("lblRoleIsRequired", "Role is required") }]}
          >
            <Select
              data-e2e="user-form-roles"
              onChange={handleRoleChange}
              showSearch={true}
              filterOption={(input, option) => dropdownFilterOption(input, option)}
              disabled={isReadOnly || isSelectedUserReadOnly}
              size={formFieldsSize}
            >
              {roles.map((rl) => {
                return (
                  <Option key={rl._id} value={rl.roleRef}>
                    {rl.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {user.provider === "local" && renderPasswordFields()}
    </Form>
  );
});

export { UserForm };
